import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  UserPlus, 
  Mail, 
  Phone, 
  Trash2, 
  Bell,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  PlayCircle,
  AlertCircle
} from "lucide-react";
import { getRoleColor, getInitials } from "@/lib/roleColors";
import { AssignmentStatusBadge } from "./AssignmentStatusBadge";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { logAuditAction } from "@/lib/permissions";
import confetti from "canvas-confetti";

interface Assignee {
  id: string;
  full_name: string;
  member_role: string;
  assignment_status: string;
  assigned_at: string;
  accepted_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  email?: string;
  phone?: string;
  assignment_id: string;
  una_member_id: string;
}

interface TaskAssigneeSectionProps {
  task: any;
  assignees: Assignee[];
  onUpdate: () => void;
  onAddAssignee: () => void;
}

export function TaskAssigneeSection({ 
  task, 
  assignees, 
  onUpdate,
  onAddAssignee 
}: TaskAssigneeSectionProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<Assignee | null>(null);
  const [removalReason, setRemovalReason] = useState("");
  const [reminderMessage, setReminderMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [canAssign, setCanAssign] = useState(false);

  // Check permissions
  useState(() => {
    if (task.una_id && user) {
      hasPermission(task.una_id, 'tasks.assign').then(setCanAssign);
    }
  });

  const currentUserAssignment = assignees.find(a => 
    a.una_member_id && user?.id // Need to match una_member.user_id with current user
  );

  const handleRemoveAssignee = async () => {
    if (!selectedAssignee) return;
    setActionLoading(true);

    try {
      // Call edge function to remove assignee
      const { data, error } = await supabase.functions.invoke('remove-task-assignee', {
        body: {
          assignmentId: selectedAssignee.assignment_id,
          taskId: task.id,
          unaId: task.una_id,
          reason: removalReason,
        },
      });

      if (error) throw error;

      toast({
        title: "Assignee Removed",
        description: `${selectedAssignee.full_name} has been removed from this task`,
      });

      setRemoveDialogOpen(false);
      setRemovalReason("");
      setSelectedAssignee(null);
      onUpdate();
    } catch (error: any) {
      console.error('Error removing assignee:', error);
      toast({
        title: "Error",
        description: "Failed to remove assignee",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendReminder = async () => {
    if (!selectedAssignee) return;
    setActionLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-task-reminder', {
        body: {
          taskId: task.id,
          assigneeEmail: selectedAssignee.email,
          assigneeName: selectedAssignee.full_name,
          taskTitle: task.task_title,
          taskDescription: task.task_description,
          dueDate: task.due_date,
          customMessage: reminderMessage,
          unaId: task.una_id,
        },
      });

      if (error) throw error;

      toast({
        title: "Reminder Sent",
        description: `Reminder sent to ${selectedAssignee.full_name}`,
      });

      if (task.una_id) {
        await logAuditAction(
          task.una_id,
          'send_task_reminder',
          'task',
          task.id,
          { assignee: selectedAssignee.full_name }
        );
      }

      setReminderDialogOpen(false);
      setReminderMessage("");
      setSelectedAssignee(null);
    } catch (error: any) {
      console.error('Error sending reminder:', error);
      toast({
        title: "Error",
        description: "Failed to send reminder",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusAction = async (action: 'accept' | 'decline' | 'start' | 'complete' | 'block') => {
    if (!currentUserAssignment) return;
    setActionLoading(true);

    try {
      const statusMap = {
        accept: 'accepted',
        decline: 'declined',
        start: 'in_progress',
        complete: 'completed',
        block: 'blocked',
      };

      const timestampMap = {
        accept: 'accepted_at',
        start: 'started_at',
        complete: 'completed_at',
      };

      const updateData: any = {
        status: statusMap[action],
      };

      if (timestampMap[action as keyof typeof timestampMap]) {
        updateData[timestampMap[action as keyof typeof timestampMap]] = new Date().toISOString();
      }

      const { error } = await supabase
        .from('task_assignments')
        .update(updateData)
        .eq('id', currentUserAssignment.assignment_id);

      if (error) throw error;

      // Show celebration for completion
      if (action === 'complete') {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }

      toast({
        title: action === 'complete' ? "🎉 Task Completed!" : "Status Updated",
        description: `Task ${action === 'accept' ? 'accepted' : action === 'complete' ? 'completed' : 'status updated'}`,
      });

      if (task.una_id) {
        await logAuditAction(
          task.una_id,
          `assignment_${action}`,
          'task',
          task.id,
          { assignment_id: currentUserAssignment.assignment_id }
        );
      }

      onUpdate();
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Empty state
  if (!assignees || assignees.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">👥 Assigned Members</h3>
        </div>
        
        <Card>
          <CardContent className="py-12 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <UserPlus className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium">No members assigned to this task</p>
                <p className="text-sm text-muted-foreground mt-1">Assign members to delegate this work</p>
              </div>
              {canAssign && (
                <Button onClick={onAddAssignee} className="mt-2">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign Members
                </Button>
              )}
              {!canAssign && (
                <p className="text-xs text-muted-foreground">Contact the task creator to add assignees</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium">
          👥 Assigned Members <Badge variant="secondary" className="ml-2">{assignees.length}</Badge>
        </h3>
        {canAssign && (
          <Button variant="outline" size="sm" onClick={onAddAssignee} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Add More
          </Button>
        )}
      </div>

      {/* Assignee Cards */}
      <div className="space-y-3">
        {assignees.map((assignee) => {
          const roleColors = getRoleColor(assignee.member_role);
          const isCurrentUser = currentUserAssignment?.una_member_id === assignee.una_member_id;

          return (
            <Card key={assignee.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div
                    className="w-12 h-12 rounded-full border-4 flex items-center justify-center text-lg font-bold flex-shrink-0"
                    style={{
                      borderColor: roleColors.border,
                      backgroundColor: roleColors.bg,
                      color: roleColors.text,
                    }}
                  >
                    {getInitials(assignee.full_name)}
                  </div>

                  {/* Member Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-lg">{assignee.full_name}</h4>
                        <Badge
                          variant="outline"
                          className="mt-1"
                          style={{
                            borderColor: roleColors.border,
                            color: roleColors.text,
                          }}
                        >
                          {roleColors.name}
                        </Badge>

                        {/* Contact Info */}
                        {assignee.email && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                            <Mail className="h-3 w-3" />
                            <a href={`mailto:${assignee.email}`} className="hover:text-primary">
                              {assignee.email}
                            </a>
                          </div>
                        )}
                        {assignee.phone && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                            <Phone className="h-3 w-3" />
                            <a href={`tel:${assignee.phone}`} className="hover:text-primary">
                              {assignee.phone}
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Status & Timestamps */}
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <AssignmentStatusBadge status={assignee.assignment_status as any} />
                        <div className="text-xs text-muted-foreground text-right">
                          <p>Assigned {formatDistanceToNow(new Date(assignee.assigned_at), { addSuffix: true })}</p>
                          {assignee.accepted_at && (
                            <p>Accepted {formatDistanceToNow(new Date(assignee.accepted_at), { addSuffix: true })}</p>
                          )}
                          {assignee.started_at && (
                            <p>Started {formatDistanceToNow(new Date(assignee.started_at), { addSuffix: true })}</p>
                          )}
                          {assignee.completed_at && (
                            <p>Completed {formatDistanceToNow(new Date(assignee.completed_at), { addSuffix: true })}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {/* Buttons for Current User */}
                      {isCurrentUser && (
                        <>
                          {assignee.assignment_status === 'pending' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => handleStatusAction('accept')}
                                disabled={actionLoading}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Accept Task
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => handleStatusAction('decline')}
                                disabled={actionLoading}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Decline
                              </Button>
                            </>
                          )}
                          {assignee.assignment_status === 'accepted' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleStatusAction('start')}
                              disabled={actionLoading}
                            >
                              <PlayCircle className="h-4 w-4 mr-1" />
                              Start Working
                            </Button>
                          )}
                          {assignee.assignment_status === 'in_progress' && (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => handleStatusAction('complete')}
                                disabled={actionLoading}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Mark Complete
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => handleStatusAction('block')}
                                disabled={actionLoading}
                              >
                                <AlertCircle className="h-4 w-4 mr-1" />
                                Block Task
                              </Button>
                            </>
                          )}
                        </>
                      )}

                      {/* Management Buttons (for task creator/treasury secretary) */}
                      {canAssign && !isCurrentUser && (
                        <>
                          {(assignee.assignment_status === 'pending' || 
                            (task.due_date && new Date(task.due_date) < new Date())) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedAssignee(assignee);
                                setReminderDialogOpen(true);
                              }}
                            >
                              <Bell className="h-4 w-4 mr-1" />
                              Send Reminder
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              setSelectedAssignee(assignee);
                              setRemoveDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Remove Assignee Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selectedAssignee?.full_name} from this task?</AlertDialogTitle>
            <AlertDialogDescription>
              This person will no longer have access to this task and will receive a notification.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Reason for removal (optional, will be sent to member)"
              value={removalReason}
              onChange={(e) => setRemovalReason(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveAssignee}
              disabled={actionLoading}
              className="bg-destructive hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Reminder Dialog */}
      <AlertDialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Reminder to {selectedAssignee?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              A reminder email will be sent about this task.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Add custom message (optional)"
              value={reminderMessage}
              onChange={(e) => setReminderMessage(e.target.value)}
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendReminder} disabled={actionLoading}>
              Send Reminder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
